import React, {
  useCallback, useContext, useEffect, useRef, useState
} from 'react';
import {
  extend, hoist, QueryContext, QueryProvider
} from '@thd-nucleus/data-sources';
import { ExperienceContext, useStore } from '@thd-nucleus/experience-context';
import { node } from 'prop-types';
import { PIPRedirector } from '@thd-nucleus/app-render';
import { AisleBay } from '@thd-olt-component-react/aisle-bay';
// @TODO: James to look at multiple `clientOnlyProduct` firing (one w/ zip, one w/o) from buybox(?)
import { Buybox } from '@thd-olt-component-react/buybox';
import { CategoryList } from '@thd-olt-component-react/category-list';
import { CompareTray } from '@thd-olt-component-react/compare';
import { Button, Col, Row } from '@thd-olt-component-react/core-ui';
import { HdhomeSignup } from '@thd-olt-component-react/email-signup';
import { LayoutGroup, LayoutManager } from '@thd-olt-component-react/layout-manager';
import { MediaGalleryWithHotspotsCarousel } from '@thd-olt-component-react/media-gallery';
import { Metadata, ProductEntryMetadata } from '@thd-olt-component-react/metadata';
import { OrderSample } from '@thd-olt-component-react/order-sample';
import { Promotion } from '@thd-olt-component-react/promotion';
import { Price } from '@thd-olt-component-react/price';
import { ProductDetails } from '@thd-olt-component-react/product-details';
import { ProductInfoBar } from '@thd-olt-component-react/product-info-bar';
import { ProductPodDefault } from '@thd-olt-component-react/product-pod';
import { Favorite } from '@thd-olt-component-react/save-to-favorites';
import {
  parseUrl,
  productDefaultResultsDataModel,
  ProductResults,
  ProductResultsContext,
  ResultsApplied,
  ResultsDimensions,
  ResultsHeader,
  ResultsPagination,
  ResultsSortBy,
  ResultsWrapped,
  updateQueryParams,
  useProductResults
} from '@thd-olt-component-react/product-results';
import { RecommendationsPlaceholder, RecommendationsProvider } from '@thd-olt-component-react/recommendations';
import { DynamicRecsWrapper, RecentlyViewedWrapper } from '@thd-olt-component-react/thd-recs-containers';
import { Link, useHistory, useLocation, useParams } from '@thd-olt-component-react/router';
import { SalientPoints } from '@thd-olt-component-react/salient-points';
import { SearchFeedback } from '@thd-olt-component-react/search-feedback';
import { SuperSku } from '@thd-olt-component-react/super-sku';
import { declareContexts, Hydrator } from '@thd-olt-component-react/hydrator';
import { Breadcrumbs, BreadcrumbsLoad, BreadcrumbsModel } from './dynamic-imports';
import { hasInStoreFilter } from '../../utils';
import { pepCampaignId, subscriptionType, requestKey } from '../../constants';
import '../../styles/pep-page.scss';

const HoistedResultsWrapped = hoist(ResultsWrapped, ProductPodDefault);

const ZoneCard = ({ children }) => {
  return <div className="zone-card">{children}</div>;
};

ZoneCard.propTypes = {
  children: node.isRequired
};

export const Pep = () => {
  declareContexts([ProductResultsContext, QueryContext, ExperienceContext]);
  const pageSize = 24;
  const { isDebugMode, clientStore } = useContext(ExperienceContext);
  const { itemId } = useParams();
  const { isClientResolved } = useContext(QueryContext);
  const history = useHistory();
  const store = useStore({ varnish: false });
  const { storeId, storeZip } = store;
  const { search: querystring, pathname } = useLocation();
  const path = `${pathname}${querystring}`;

  const resx = {
    appId: 'homedepot01',
    card: true,
    carouselOptions: {
      fullScrollSkip: 2
    },
    criteo: {
      version: 'model2'
    },
    event: 'product',
    maxProducts: 16,
    products: itemId,
    showATC: true
  };

  const scrollToRef = React.createRef();

  const onStoreChange = ({ output }) => {
    const { storeId: localStore } = output || {};
    if (!storeId) return;

    if (window && window.THD_LOCALIZER_AUTO_INIT && window.THD_LOCALIZER_AUTO_INIT.Localizer) {
      window.THD_LOCALIZER_AUTO_INIT.Localizer
        .localize({ storeId: localStore })
        .then(() => {
          window.location.reload();
        });
    }
  };

  useEffect(() => {
    window.LIFE_CYCLE_EVENT_BUS.on('product-results.change-store', onStoreChange);
  }, []);

  const targetPosition = (element) => {
    if (!element) return null;
    const currentPosition = window.scrollY || window.pageYOffset;
    return element.getBoundingClientRect().top + currentPosition;
  };

  const onDimensionsChange = useCallback(({ refinement, dimension = '' }) => {
    const { redirectUrl, url, clear = false, refinementKey } = refinement;
    const params = {
      ...parseUrl({ path }),
      ...parseUrl({ path: url }),
      Nao: 0
    };

    if (clear || (dimension && dimension?.label === 'Price' && !refinementKey)) {
      params.lowerbound = '';
      params.upperbound = '';
    }

    const paramsObject = Object.keys(params)
      .map((param) => {
        return {
          paramName: param,
          paramValue: params[param]
        };
      })
      .filter((par) => {
        return !/category|context|slugs/.test(par.paramName);
      });
    const updatedUrl = redirectUrl || updateQueryParams(path, paramsObject);
    window.LIFE_CYCLE_EVENT_BUS.trigger('filters-refinements.click', refinement);
    const targetNode = scrollToRef && scrollToRef.current
      ? scrollToRef.current
      : null;
    let targetLocation = targetPosition(targetNode);
    window.scrollTo({
      top: targetLocation,
      behavior: 'smooth'
    });
    history.push(updatedUrl);
  });

  const onPageChange = useCallback(({ page }) => {
    const newPath = updateQueryParams(path, [
      {
        paramName: 'Nao',
        paramValue: (page - 1) * (pageSize)
      }
    ]);

    history.push(newPath);

    const targetNode = scrollToRef && scrollToRef.current
      ? scrollToRef.current
      : null;
    let targetLocation = targetPosition(targetNode);
    window.scrollTo({
      top: targetLocation,
      behavior: 'smooth'
    });
  });

  const onSortChange = useCallback(({
    sortby, sortorder, name, currentPage, page
  }) => {
    const newPath = updateQueryParams(path, [
      {
        paramName: 'Nao',
        paramValue: null
      },
      {
        paramName: 'sortorder',
        paramValue: sortorder
      },
      {
        paramName: 'sortby',
        paramValue: sortby
      }
    ]);

    window.LIFE_CYCLE_EVENT_BUS.trigger('sort-refinements.click', {
      name,
      currentPage,
      page
    });
    history.push(newPath);
  });

  const onChange = (dataSku = {}) => {
    const { slug, itemId: sskuItemId } = dataSku;
    if (slug && sskuItemId) {
      history.push(`/pep/${slug}/${sskuItemId}`);
    }
  };

  const cleanPath = path.replace(/^\/render[^/]*/, '');

  const {
    keyword,
    lowerbound,
    Nao = 0,
    nValue,
    sortby,
    sortorder,
    upperbound
  } = parseUrl({ path: cleanPath });

  const prcStoreId = useRef(storeId);

  // show the sponsored products
  const additionalSearchParams = {
    sponsored: ['bestmatch', 'topsellers'].indexOf(sortby) > -1
  };

  let THDCustomer = {};

  if (clientStore) {
    THDCustomer = typeof window !== 'undefined' ? window.THDCustomer?.default : {};
  }

  if (Object.keys(THDCustomer).length !== 0 && THDCustomer.svocID) {
    additionalSearchParams.svocID = THDCustomer.svocID;
  }

  const { data, error, loading, variables } = useProductResults({
    additionalSearchParams,
    itemId,
    keyword,
    lowerbound,
    nValue,
    pageSize,
    sortby,
    sortorder,
    start: Nao,
    storeId,
    upperbound
  });

  if (data && !loading) {
    prcStoreId.current = variables.storeId;
  }

  const { products = [] } = data?.searchModel || {};
  const pipPath = pathname && pathname.replace(/\/pep\//gi, '/p/');
  const isInStoreFilterON = hasInStoreFilter(data);

  useEffect(() => {
    LIFE_CYCLE_EVENT_BUS.lifeCycle.trigger('pep.pip-ready', { itemId });
  }, [itemId]);

  const stringifyItemIds = (productsList) => {
    return productsList?.map(({ itemId: sku }) => sku)
      .join('-');
  };

  useEffect(() => {
    if (data && !loading) {
      LIFE_CYCLE_EVENT_BUS.lifeCycle.trigger('pep.new-products', { ...data?.searchModel, defaultPageSize: pageSize });
    }
  }, [stringifyItemIds(products)]);

  const [selectedForCompare, setSelectedForCompare] = useState([]);

  const onSelectedForCompare = (id, selected = false) => {
    if (selected) {
      setSelectedForCompare([...selectedForCompare, id]);
    } else {
      setSelectedForCompare(selectedForCompare.filter((item) => item !== id));
    }
  };

  const onCompare = () => {
    const queryparams = selectedForCompare
      .map((item, idx) => `item${idx + 1}=${item}`)
      .join('&');

    history.push({
      pathname: '/compare',
      search: `?${queryparams}`
    }, {
      bypassExperienceCheck: true
    });
  };

  useEffect(() => {
    const itemIds = window.sessionStorage.getItem('compare');
    window.sessionStorage.removeItem('compare');
    if (itemIds) {
      setSelectedForCompare(JSON.parse(itemIds));
    }
  }, []);

  const defaultVariables = {
    storeId,
    zipCode: storeZip,
    skipKPF: true,
    skipSubscribeAndSave: true
  };
  const skipFn = ({ skip, queryName, attributes }) => {
    if (queryName !== 'product') {
      return skip;
    }
    const isResolved = isClientResolved({ queryName: 'searchModel' });
    if (attributes.product.fulfillment) {
      if (!isResolved) {
        return true;
      }
    }
    return skip;
  };
  return (
    <div className="pep-page">
      <RecommendationsProvider resx={resx}>
        <Row className="isBound">
          <LayoutManager
            name="pep"
            useContextForProps
          >
            <LayoutGroup name="redirector">
              <PIPRedirector
                disableCanonicalRedirect
                disableCMMMCRedirect
              />
            </LayoutGroup>
            <LayoutGroup name="pep-metadata">
              <Metadata>
                <ProductEntryMetadata itemId={itemId} />
              </Metadata>
            </LayoutGroup>
            <LayoutGroup name="based-on-your-search">
              <Row>
                <Col className="based-on-your-search">
                  Based on your search:
                </Col>
              </Row>
            </LayoutGroup>
            <LayoutGroup
              className="zone-a"
              name="zone-a"
              tag={Row}
            >
              <LayoutManager
                name="zone-a"
                useContextForProps
              >
                <LayoutGroup
                  className="zone-a__card"
                  fallback="10"
                  id="zone-a-product"
                  name="zone-a-product"
                  sm="10"
                  tag={Col}
                  xs="9"
                >
                  <LayoutGroup
                    className="media-section"
                    fallback="8"
                    name="media"
                    sm="4"
                    tag={Col}
                    xs="6"
                  >
                    <ProductInfoBar itemId={itemId} />
                    <div className="product-details__favorites">
                      <Favorite
                        itemId={itemId}
                        showCircle
                        showCount
                      />
                    </div>
                    <MediaGalleryWithHotspotsCarousel
                      hover
                      itemId={itemId}
                    />
                  </LayoutGroup>
                  <LayoutGroup
                    className="info-section"
                    fallback="4"
                    name="info"
                    sm="4"
                    tag={Col}
                    xs="6"
                  >
                    <LayoutManager
                      name="info-section"
                      useContextForProps
                    >
                      <LayoutGroup name="product-details">
                        <ProductDetails itemId={itemId}>
                          <div className="product-details__badge-title--wrapper">
                            <ProductDetails.Badge itemId={itemId} />
                            <ProductDetails.Title link />
                          </div>
                          <ProductDetails.BrandCollection showCollection />
                          <ProductDetails.Ratings
                            offsetselector="#sticky-nav"
                            pageType="PEP"
                            qaselector="#questions_answers_full"
                            showBadge={false}
                            target="#ratings-and-reviews"
                            disableReviewsIfZero
                          />
                        </ProductDetails>
                      </LayoutGroup>
                      <LayoutGroup name="aisle-bay">
                        <AisleBay itemId={itemId} />
                      </LayoutGroup>
                      <LayoutGroup name="salient-points">
                        <SalientPoints
                          hideIfMultiVariant
                          itemId={itemId}
                          max={3}
                        />
                      </LayoutGroup>
                      <LayoutGroup name="super-sku">
                        <SuperSku
                          itemId={itemId}
                          onChange={onChange}
                        />
                      </LayoutGroup>
                      <LayoutGroup name="order-sample">
                        <OrderSample
                          itemId={itemId}
                          link
                          oosMessage
                        >
                          Need a closer look?
                        </OrderSample>
                      </LayoutGroup>
                    </LayoutManager>
                  </LayoutGroup>
                  <LayoutGroup
                    className="checkout-section"
                    name="checkout"
                    sm="4"
                    tag={Col}
                  >
                    <LayoutManager
                      name="buy-area"
                      useContextForProps
                    >
                      <LayoutGroup name="price">
                        <Price
                          channel="desktop"
                          disableRangePricing
                          displayEachUom={false}
                          itemId={itemId}
                          large
                          clsRemediation={{
                            placeholders: true,
                            preservePlaceholders: true
                          }}
                        />
                      </LayoutGroup>
                      <LayoutGroup
                        name="promotion"
                      >
                        <div className="promotion-card">
                          <Promotion
                            channel="desktop"
                            storeId={storeId}
                            itemId={itemId}
                          />
                        </div>
                      </LayoutGroup>
                      <LayoutGroup
                        name="buybox"
                        tag={Col}
                      >
                        <Buybox
                          hideInstantCheckout
                          hidePaypalCheckout
                          itemId={itemId}
                          lite
                        />
                        <LayoutGroup name="details">
                          <div className="see-details">
                            <Button
                              className="see-details__box"
                              outline
                            >
                              <Link
                                className="see-details__link"
                                target="_blank"
                                to={pipPath}
                              >View Full Product Details
                              </Link>
                            </Button>
                          </div>
                        </LayoutGroup>
                      </LayoutGroup>

                    </LayoutManager>
                  </LayoutGroup>
                </LayoutGroup>
                <LayoutGroup
                  className="zone-a__category-list"
                  fallback="2"
                  name="category-list"
                  sm="2"
                  tag={Col}
                  xs="3"
                >
                  <CategoryList
                    containerId="zone-a-product"
                    displayType="card"
                    itemId={itemId}
                  />
                </LayoutGroup>
              </LayoutManager>
              <LayoutManager
                input={{ isDebugMode }}
                name="zone-b"
                useContextForProps
              >
                <DynamicRecsWrapper
                  schemaName="pipsem"
                  anchorId={itemId}
                  hideATC
                  requestKey={requestKey}
                  errorBoundary
                  hydrator={{
                    className: 'grid',
                    tag: ZoneCard,
                    id: `pipsem-${itemId}`,
                    scrollBuffer: 0,
                    delay: 0
                  }}
                />

                <LayoutGroup
                  name="breadcrumbs"
                  tag={Col}
                >
                  <Hydrator
                    className="grid"
                    delay={3000}
                    id="pep-breadcrumbs"
                    scrollBuffer={1500}
                    waitFor={[BreadcrumbsLoad]}
                  >
                    <Breadcrumbs
                      data={data}
                      error={error}
                      loading={loading}
                    />
                  </Hydrator>
                </LayoutGroup>

                <LayoutGroup
                  name="product-results"
                  tag={Col}
                >
                  <Hydrator
                    className="grid"
                    delay={3000}
                    id="pep-product-results"
                    preserveCtxVal="clientStore"
                    scrollBuffer={1500}
                  >
                    <span ref={scrollToRef} />
                    <ProductResults
                      data={data}
                      error={error}
                      loading={loading}
                    >
                      <Row>
                        <Col nopadding>
                          <LayoutGroup
                            name="results-header"
                            tag={Col}
                          >
                            <ResultsHeader preamble="Similar" />
                          </LayoutGroup>
                        </Col>
                      </Row>
                      <Row>
                        <Col
                          nopadding
                          sm="9"
                        >
                          <LayoutGroup
                            name="results-applied"
                            tag={Col}
                          >
                            <ResultsApplied onAppliedChange={onDimensionsChange} />
                          </LayoutGroup>
                        </Col>
                        <Col
                          nopadding
                          xs="3"
                        >
                          <LayoutGroup
                            name="results-sortby"
                            tag={Col}
                          >
                            <ResultsSortBy onSortChange={onSortChange} />
                          </LayoutGroup>
                        </Col>
                      </Row>
                      <Row className="u--paddingBottom">
                        <Col
                          lg="2"
                          md="2"
                          nopadding
                          sm="3"
                          xs="4"
                        >
                          <LayoutGroup
                            name="results-dimensions"
                            tag={Col}
                          >
                            <ResultsDimensions
                              enableVisualRefinement
                              onDimensionsChange={onDimensionsChange}
                              enableMultiStore
                            />
                          </LayoutGroup>
                        </Col>
                        <Col
                          lg="10"
                          md="10"
                          nopadding
                          sm="9"
                          xs="8"
                        >
                          <LayoutGroup
                            name="wrapped-results"
                            tag={Col}
                          >
                            <Row>
                              <Col nopadding>
                                <QueryProvider
                                  cacheKey="pep-product-pods"
                                  dataSource="searchNav"
                                  defaultVariables={defaultVariables}
                                  skip={skipFn}
                                >
                                  <HoistedResultsWrapped>
                                    {products.map((product, itemIndex) => {
                                      const { itemId: skuId } = product?.identifiers || {};
                                      if (!skuId) return null;
                                      return (
                                        <LayoutGroup
                                          id={`plp-product-pod__${itemIndex}`}
                                          index={itemIndex}
                                          key={`${skuId}-${itemIndex}`}
                                          name={`Product Pod: ${itemIndex}`}
                                        >
                                          <Col
                                            className="pep__pod"
                                            fallback="6"
                                            lg="3"
                                            md="3"
                                            sm="3"
                                            xs="4"
                                          >
                                            <ProductPodDefault
                                              hover
                                              itemId={skuId}
                                              mobilePodType="doubleColumn"
                                              showFavoritesCount
                                              storeId={prcStoreId.current}
                                              hasInStoreFilter={isInStoreFilterON}
                                              target="_blank"
                                              comparable
                                              onCompare={onCompare}
                                              selectedForCompare={selectedForCompare}
                                              onSelectedForCompare={onSelectedForCompare}
                                            />
                                          </Col>
                                        </LayoutGroup>
                                      );
                                    })}
                                  </HoistedResultsWrapped>
                                </QueryProvider>
                              </Col>
                            </Row>
                            <Row className="u--paddingTop u--paddingBottom">
                              <Col nopadding>
                                <LayoutGroup
                                  name="results-pagination"
                                  tag={Col}
                                >
                                  <ResultsPagination
                                    onPageChange={onPageChange}
                                    pageSize={pageSize}
                                  />
                                </LayoutGroup>
                                <LayoutGroup
                                  name="search feedback"
                                  tag={Col}
                                >
                                  <SearchFeedback />
                                </LayoutGroup>
                              </Col>
                            </Row>
                          </LayoutGroup>
                        </Col>
                      </Row>
                    </ProductResults>
                  </Hydrator>
                </LayoutGroup>
                <LayoutGroup
                  name="promotional"
                  tag={Col}
                >
                  <HdhomeSignup formSubmission subscriptionType={subscriptionType} campaignId={pepCampaignId} />
                </LayoutGroup>
                <LayoutGroup
                  name="recs-pip-sponsored"
                  tag={Row}
                >
                  <Col nopadding>
                    <RecommendationsPlaceholder
                      defer
                      scheme="pip_sponsored"
                    />
                  </Col>
                </LayoutGroup>
                <QueryProvider cacheKey="rv-pep-recs" persist>
                  <RecentlyViewedWrapper
                    schemaName="rv_gm_pip_rr"
                    errorBoundary
                    hydrator={{
                      className: 'grid',
                      tag: ZoneCard,
                      id: 'rv_gm_pip_rr',
                      scrollBuffer: 1500,
                      delay: 3000
                    }}
                  />
                </QueryProvider>
                <LayoutGroup tag={Row} className="u--paddingTop u--paddingBottom" name="compare">
                  <Col>
                    <CompareTray
                      itemIds={selectedForCompare}
                      onRemove={onSelectedForCompare}
                      onCompare={onCompare}
                    />
                  </Col>
                </LayoutGroup>
              </LayoutManager>
            </LayoutGroup>
          </LayoutManager>
        </Row>
      </RecommendationsProvider>
    </div>
  );
};

Pep.displayName = 'PepPage';

Pep.propTypes = {};

Pep.defaultProps = {};

Pep.dataModel = extend(
  PIPRedirector,
  AisleBay,
  BreadcrumbsModel,
  Buybox,
  CategoryList,
  ResultsDimensions,
  CompareTray,
  HdhomeSignup,
  MediaGalleryWithHotspotsCarousel,
  OrderSample,
  Price,
  Promotion,
  ProductDetails,
  ProductInfoBar,
  RecommendationsPlaceholder,
  Favorite,
  SalientPoints,
  SuperSku,
  productDefaultResultsDataModel,
  DynamicRecsWrapper,
  RecentlyViewedWrapper
);
